const STORAGE_KEY = 'ob_utm';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_lead'] as const;
type UtmKey = (typeof UTM_KEYS)[number];

export type Utm = Partial<Record<UtmKey, string>>;

/**
 * Short spelling of utm_lead for links pasted into hand-written emails
 * (`/watch/receiving?s=jrs`). An explicit utm_lead wins.
 */
const LEAD_ALIAS = 's';

/** Parse the UTM params from a search string (defaults to the current URL). */
export function readUtm(search: string = safeSearch()): Utm {
  const params = new URLSearchParams(search);
  const utm: Utm = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) {
      utm[key] = value;
    }
  }

  const lead = params.get(LEAD_ALIAS);
  if (lead && !utm.utm_lead) {
    utm.utm_lead = lead;
  }

  return utm;
}

/**
 * Merge freshly-read URL params over any already-persisted set, then store.
 * Only keys present in the new read overwrite, so an original utm_lead survives
 * a later in-app read that lacks it. Returns the merged result.
 */
export function persistUtm(storage: Storage): Utm {
  const merged = { ...readStore(storage), ...readUtm() };
  writeStore(storage, merged);

  return merged;
}

/**
 * Persist UTMs to the given storage kind, guarding the storage *access* itself
 * (blocked-storage browsers throw on `window.localStorage`). Falls back to the
 * current merged view if storage is unavailable. Never throws.
 */
export function captureUtm(kind: 'session' | 'local'): Utm {
  const storage = safeStorage(kind);

  return storage ? persistUtm(storage) : getUtm();
}

/**
 * Storage-agnostic read used by every downstream consumer. Merges both
 * storages, localStorage winning on conflict (the app's durable stash).
 */
export function getUtm(): Utm {
  return { ...readStore(safeStorage('session')), ...readStore(safeStorage('local')) };
}

/** Clear the persisted UTMs from both storages. */
export function clearUtm(): void {
  removeItem(safeStorage('session'));
  removeItem(safeStorage('local'));
}

/** Return `url` with `utm` appended; existing query preserved, our keys win. */
export function appendUtm(url: string, utm: Utm): string {
  const entries = Object.entries(utm).filter(([, v]) => v != null && v !== '');
  if (entries.length === 0) {
    return url;
  }

  // Relative URLs (e.g. "/signup") need a base for the URL constructor.
  const parsed = new URL(url, safeOrigin());
  for (const [key, value] of entries) {
    parsed.searchParams.set(key, value as string);
  }

  // Preserve the caller's absolute/relative shape.
  return /^https?:\/\//i.test(url) ? parsed.toString() : parsed.pathname + parsed.search;
}

/** Flatten a Utm into Plausible props, omitting undefined/empty keys. */
export function utmProps(utm: Utm): Record<string, string> {
  const props: Record<string, string> = {};
  for (const [key, value] of Object.entries(utm)) {
    if (value != null && value !== '') {
      props[key] = value;
    }
  }

  return props;
}

// --- storage plumbing (never throws; degrades to {} / no-op) ---

function readStore(storage: Storage | null): Utm {
  if (!storage) {
    return {};
  }

  try {
    const raw = storage.getItem(STORAGE_KEY);

    return raw ? (JSON.parse(raw) as Utm) : {};
  } catch {
    return {};
  }
}

function writeStore(storage: Storage, utm: Utm): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(utm));
  } catch {
    // private-mode quota / disabled storage — attribution is best-effort
  }
}

function removeItem(storage: Storage | null): void {
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // blocked-storage policies can throw on the method call itself, not just
    // on accessing the storage object — degrade to a no-op either way
  }
}

function safeStorage(kind: 'session' | 'local'): Storage | null {
  try {
    return kind === 'session' ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

function safeSearch(): string {
  try {
    return window.location.search;
  } catch {
    return '';
  }
}

function safeOrigin(): string {
  try {
    return window.location.origin;
  } catch {
    return 'http://localhost';
  }
}
